import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import { FormSection } from '../philData';

// Format an ISO submission timestamp for display (e.g. "29 Jun 2026").
function formatSubmittedAt(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ApplicationReview() {
  const navigate = useNavigate();
  const { data: reviewData, loading, error } = useApi(() => API.applicationReview());
  const { data: schemaData } = useApi(() => API.applicationSchema());
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const sectionRouteById = (id: string, sections: FormSection[]) => {
    const idx = sections.findIndex(s => s.id === id);
    return `/apply/section-${idx >= 0 ? idx + 1 : 1}`;
  };

  const onSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await API.submitApplication();
      setSubmitted(true);
    } catch {
      setSubmitError('Submission failed. Please ensure all required fields are complete and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !reviewData) return <div className="phil-error">Unable to load review. {error}</div>;

  const sections: any[] = reviewData.sections;
  const schemaSections: FormSection[] = schemaData?.sections ?? [];
  const lastSectionNum = schemaSections.length;
  const incompleteSections = sections.filter((s: any) => s.status === 'Incomplete');
  // Once submitted (server-side or just now in this session) the application is locked.
  const locked = reviewData.submitted || submitted;

  return (
    <>
      {/* Step progress — all section steps done, Review is current */}
      <div className="phil-steps">
        {schemaSections.map((s: FormSection) => (
          <React.Fragment key={s.id}>
            <div className="phil-step">
              <div className="phil-step-dot done">✓</div>
              <span className="phil-step-label done">{s.shortLabel}</span>
            </div>
            <div className="phil-step-conn" />
          </React.Fragment>
        ))}
        <div className="phil-step">
          <div className="phil-step-dot current">★</div>
          <span className="phil-step-label current">Review</span>
        </div>
      </div>

      <div className="phil-page-heading" style={{ fontSize: 18 }}>
        {locked ? 'Your Submitted Application' : 'Review Your Application'}
      </div>
      <div className="phil-page-sub">
        {locked
          ? 'Your application has been submitted and is now read-only.'
          : <>All required fields (<span style={{ color: '#e53e3e' }}>*</span>) must be answered before submitting.</>}
      </div>

      {/* Blocking banner shown when required fields are missing (pre-submission only) */}
      {!locked && incompleteSections.length > 0 && (
        <div className="phil-warn-banner">
          ⚠ {incompleteSections.length} section{incompleteSections.length > 1 ? 's have' : ' has'} unanswered required fields. Complete them before submitting.
        </div>
      )}

      <div className="phil-card">
        <table className="phil-table">
          <thead>
            <tr>
              <th>Section</th>
              <th>Required Progress</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sections.map((s: any) => (
              <React.Fragment key={s.id}>
                <tr>
                  <td style={{ fontWeight: 600 }}>{s.title}</td>
                  <td style={{ color: '#555' }}>
                    {s.totalRequired > 0
                      ? `${s.answeredRequired} / ${s.totalRequired} required`
                      : <span style={{ color: '#888' }}>All optional</span>}
                  </td>
                  <td>
                    <span className={s.status === 'Complete'
                      ? 'phil-badge phil-badge-complete'
                      : 'phil-badge phil-badge-review'}>
                      {s.status}
                    </span>
                  </td>
                  <td>
                    <span
                      className="phil-section-sub"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(sectionRouteById(s.id, schemaSections))}
                    >
                      {locked ? 'View →' : 'Edit →'}
                    </span>
                  </td>
                </tr>
                {s.missingRequired?.length > 0 && (
                  <tr style={{ background: '#fff8f0' }}>
                    <td colSpan={4} style={{ paddingTop: 4, paddingBottom: 8, paddingLeft: 24 }}>
                      <div className="phil-missing-list">
                        {s.missingRequired.map((mq: any) => (
                          <span key={mq.questionId} className="phil-missing-item">
                            {mq.questionId}. {mq.label}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {!locked && (
        <div className="phil-info-note">
          By submitting, you confirm all information is accurate. Submissions cannot be edited after the deadline.
        </div>
      )}

      {submitError && (
        <div className="phil-warn-banner" style={{ marginTop: 8 }}>{submitError}</div>
      )}

      {locked ? (
        <div className="phil-saved-banner" style={{ marginTop: 16 }}>
          ✓ Application submitted{reviewData.submittedAt ? ` on ${formatSubmittedAt(reviewData.submittedAt)}` : ''}. Your application is locked and can no longer be edited.
        </div>
      ) : !submitted ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <button
            className="phil-btn phil-btn-secondary"
            onClick={() => navigate(`/apply/section-${lastSectionNum}`)}
          >
            ← Back to {schemaSections[lastSectionNum - 1]?.shortLabel ?? 'Previous'}
          </button>
          <button
            className="phil-btn phil-btn-success"
            style={{ padding: '11px 30px', fontSize: 14, opacity: reviewData.canSubmit ? 1 : 0.45 }}
            onClick={onSubmit}
            disabled={!reviewData.canSubmit || submitting}
            title={!reviewData.canSubmit ? 'Complete all required fields to submit' : undefined}
          >
            {submitting ? 'Submitting…' : '✓ Submit Application'}
          </button>
        </div>
      ) : (
        <div className="phil-saved-banner">
          ✓ Application submitted. A confirmation email with your application ID has been sent. Your application is now locked until the deadline passes.
        </div>
      )}
    </>
  );
}
